import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import liff from '@line/liff';
import { btsApi } from '../services/btsApi';
import BtsPopup from '../templates/BtsPopup';
import ScanProcessing from '../templates/ScanProcessing';
import ScanSuccess from '../templates/ScanSuccess';
import closedLogo from '../../../assets/images/game-bts/Logo-Bill-Roojai-bts.png';
import './ReceiptPage.css';
const IS_PROD = process.env.REACT_APP_ENV === 'prod';
const log = (...args) => { if (!IS_PROD) console.log(...args); };
const LIFF_ID = process.env.REACT_APP_LIFF_ID;

// =====================================================
// Error mapping — ครอบคลุมทุก case
// =====================================================
function mapError(err) {
  // รวมทุก field ที่อาจมี error code
  const errorCode = err?.data?.error || '';
  const msg = err?.message || '';
  const combined = `${errorCode} ${msg}`;
  log('[BTS Receipt] Error mapping — code:', errorCode, 'msg:', msg);

  // Employee blocked
  if (combined.includes('EMPLOYEE_BLOCKED'))    return { title: 'ไม่สามารถร่วมกิจกรรมได้', message: 'พนักงานไม่สามารถร่วมเล่นเกมได้ กรุณาติดตามกิจกรรมใหม่ในครั้งหน้า', icon: 'error' };

  // Receipt errors
  if (combined.includes('ALREADY_SCANNED'))      return { title: 'บิลซ้ำ', message: 'ใบเสร็จนี้ถูกสแกนไปแล้ว ไม่สามารถใช้ซ้ำได้', icon: 'warning' };
  if (combined.includes('BIGPOINT_MISMATCH'))    return { title: 'บิลไม่ใช่ของคุณ', message: 'ใบเสร็จนี้ไม่ตรงกับบัญชี BigPoint ของคุณ กรุณาใช้ใบเสร็จของตัวเอง', icon: 'error' };
  if (combined.includes('BIGPOINT_CHECK_FAILED'))return { title: 'ตรวจสอบไม่สำเร็จ', message: 'ไม่สามารถตรวจสอบบัญชี BigPoint กับระบบได้ กรุณาลองใหม่', icon: 'error' };
  if (combined.includes('OWNER_MISMATCH'))       return { title: 'บิลไม่ตรง', message: 'ใบเสร็จนี้ไม่ตรงกับบัญชี BigPoint ของคุณ', icon: 'error' };
  if (combined.includes('VERIFICATION_FAILED'))  return { title: 'ตรวจสอบไม่สำเร็จ', message: 'ไม่สามารถตรวจสอบใบเสร็จกับระบบได้ กรุณาลองใหม่', icon: 'error' };
  if (combined.includes('TRANSACTION_NOT_FOUND'))return { title: 'ไม่พบใบเสร็จ', message: 'ไม่พบข้อมูลใบเสร็จในระบบ กรุณาตรวจสอบ QR Code', icon: 'error' };
  if (combined.includes('MISSING_REQUIRED_FIELDS')) return { title: 'ข้อมูลไม่ครบ', message: 'ข้อมูลใบเสร็จไม่ครบถ้วน กรุณาสแกน QR ใบเสร็จ Big C ใหม่', icon: 'warning' };

  // Player errors
  if (combined.includes('PLAYER_NOT_FOUND'))     return { title: 'ไม่พบผู้เล่น', message: 'ไม่พบข้อมูลผู้เล่น กรุณาเข้าสู่ระบบใหม่', icon: 'error' };
  if (combined.includes('NO_BIGPOINT') || combined.includes('BIGPOINT_NOT_LINKED')) return { title: 'ยังไม่ได้ผูก BigPoint', message: 'กรุณาผูกบัญชี BigPoint ก่อนสแกนบิล', icon: 'warning', redirect: '/register' };

  // Party errors
  if (combined.includes('CAMPAIGN_NOT_ACTIVE'))  return { title: 'นอกช่วงเวลา', message: 'ขณะนี้อยู่นอกช่วงเวลาแคมเปญ', icon: 'info' };

  // Network
  if (err?.isNetworkError) return { title: 'ไม่มีสัญญาณ', message: 'ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต', icon: 'error' };

  // ใช้ message จาก backend ถ้ามี (เช่น errorHandler ส่งภาษาไทยมา)
  const backendTitle = err?.data?.title;
  const backendMsg = err?.data?.message;
  if (backendTitle || backendMsg) {
    return { title: backendTitle || 'เกิดข้อผิดพลาด', message: backendMsg || msg, icon: 'error' };
  }

  // Generic
  return { title: 'เกิดข้อผิดพลาด', message: msg || 'ขออภัย เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง', icon: 'error' };
}

// Campaign end at end-of-day 27 May 2026 (= 00:00 28 May Bangkok)
const CAMPAIGN_END = new Date('2026-05-28T00:00:00+07:00');
const isCampaignEnded = () => Date.now() >= CAMPAIGN_END.getTime();

const ReceiptPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('processing');
  const [result, setResult] = useState(null);
  const [popup, setPopup] = useState(null);
  const [campaignEnded, setCampaignEnded] = useState(isCampaignEnded());
  const redirectTimer = useRef(null);
  const scanCalledRef = useRef(false);

  // เช็คเที่ยงคืน — block scan flow ถ้าเลย CAMPAIGN_END แล้ว
  useEffect(() => {
    if (campaignEnded) return;
    const check = () => { if (isCampaignEnded()) setCampaignEnded(true); };
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [campaignEnded]);

  // ดึง params จาก 3 ที่: URL params → ?d=BASE64 → sessionStorage
  const savedParams = JSON.parse(sessionStorage.getItem('bts_receipt_params') || '{}');

  // decode ?d=BASE64 ถ้ามี
  const dParam = searchParams.get('d');
  const decodedParams = (() => {
    if (!dParam) return {};
    try { return JSON.parse(atob(dParam)); } catch { return {}; }
  })();

  const receiptNo = searchParams.get('receiptNo') || decodedParams.receiptNo || savedParams.receiptNo || null;
  const partnerId = searchParams.get('partnerId') || decodedParams.partnerId || savedParams.partnerId || null;
  const customer = searchParams.get('customer') || decodedParams.customer || savedParams.customer || null;
  const sale = searchParams.get('sale') || decodedParams.sale || savedParams.sale || null;
  const sale_date = searchParams.get('sale_date') || decodedParams.sale_date || savedParams.sale_date || null;
  const sale_store = searchParams.get('sale_store') || decodedParams.sale_store || savedParams.sale_store || null;

  // เก็บ params + redirect ไป LIFF ถ้าเปิดจากกล้องข้างนอก
  useEffect(() => {
    const hasUrlParams = searchParams.get('receiptNo');
    const hasDecodedParams = decodedParams.receiptNo;

    if (hasUrlParams || hasDecodedParams) {
      const paramsObj = {
        receiptNo: receiptNo,
        partnerId: partnerId,
        customer: customer,
        sale: sale,
        sale_date: sale_date,
        sale_store: sale_store,
      };
      sessionStorage.setItem('bts_receipt_params', JSON.stringify(paramsObj));

      // ถ้าไม่อยู่ใน LIFF + ไม่ได้มาจาก navigate ภายใน app → redirect ไป LIFF
      // เช็คจาก referrer หรือ sessionStorage ว่ามาจาก scan ในเกมหรือไม่
      const fromInternalScan = sessionStorage.getItem('bts_internal_scan');
      if (!fromInternalScan && !liff.isInClient()) {
        log('[BTS Receipt] Not in LIFF, redirecting with base64 params...');
        const encoded = btoa(JSON.stringify(paramsObj));
        window.location.href = `https://liff.line.me/${LIFF_ID}/game/bts/receipt?d=${encoded}`;
        return;
      }
      sessionStorage.removeItem('bts_internal_scan');
    }
  }, [searchParams]);

  log('[BTS Receipt] QR Params:', { receiptNo, partnerId, customer, sale, sale_date, sale_store });

  // auto redirect กลับหน้าแรกหลัง popup 4 วิ
  const showErrorAndRedirect = useCallback((errorInfo) => {
    setPopup(errorInfo);
    // ถ้า redirect ไป register → set flag ให้ SuccessBindingForm กลับมาเกม
    if (errorInfo.redirect === '/register') {
      sessionStorage.setItem('bts_redirect_after_register', '/game/bts');
    }
    redirectTimer.current = setTimeout(() => {
      navigate(errorInfo.redirect || '/game/bts', { replace: true });
    }, 4000);
  }, [navigate]);

  // cleanup timer
  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  useEffect(() => {
    // เกมปิดแล้ว → ไม่ยิง API scan
    if (campaignEnded) return;
    // ดัก URL params ไม่ครบ — ต้องมีครบทุกค่า
    const fields = { receiptNo, partnerId, customer, sale, sale_date, sale_store };
    const missing = Object.entries(fields).filter(([, v]) => !v).map(([k]) => k);
    if (missing.length > 0) {
      console.error('[BTS Receipt] Missing params:', missing);
      showErrorAndRedirect({
        title: 'ข้อมูลไม่ครบ',
        message: 'ข้อมูลใบเสร็จไม่ครบถ้วน กรุณาสแกน QR ใบเสร็จ Big C ใหม่',
        type: 'warning',
      });
      return;
    }

    if (scanCalledRef.current) return;
    // ใช้ sessionStorage กัน double scan เมื่อ component re-mount
    const scanKey = `bts_scanning_${receiptNo}_${customer}`;
    if (sessionStorage.getItem(scanKey)) {
      log('[BTS Receipt] Already scanning this receipt, skip');
      return;
    }
    scanCalledRef.current = true;
    sessionStorage.setItem(scanKey, '1');

    const doScan = async () => {
      try {
        await btsApi.initPlayer();

        const res = await btsApi.scanReceipt({
          receiptNo,
          partnerId,
          customer,
          sale,
          sale_date,
          sale_store,
        });

        log('[BTS Receipt] Scan result:', res);
        setResult(res?.data || res);
        sessionStorage.removeItem('bts_receipt_params');
        sessionStorage.removeItem(scanKey);
        // mark recent scan — กัน popup เด้งบน dashboard เมื่อกลับมา (90 วินาที)
        sessionStorage.setItem('bts_came_from_scan', Date.now().toString());
        // ลบ params จาก URL กัน reload แล้วยิงซ้ำ
        window.history.replaceState({}, '', '/game/bts/receipt');
      } catch (err) {
        console.error('[BTS Receipt] Scan error:', err);
        const errorInfo = mapError(err);
        showErrorAndRedirect({ ...errorInfo, type: errorInfo.icon || 'error' });
        sessionStorage.removeItem('bts_receipt_params');
        sessionStorage.removeItem(scanKey);
        sessionStorage.setItem('bts_came_from_scan', Date.now().toString());
        window.history.replaceState({}, '', '/game/bts/receipt');
      }
    };

    doScan();
  }, [receiptNo, partnerId, customer, sale, sale_date, sale_store, showErrorAndRedirect]);

  // เปลี่ยนหน้าเมื่อ result มีค่า (API ตอบแล้ว)
  useEffect(() => {
    if (result && !popup) {
      setStep('success');
    }
  }, [result, popup]);

  const handleClose = () => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
    navigate('/game/bts', { replace: true });
  };

  const handleCreateTeam = () => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
    // ไปหน้าเกม + เปิดแท็บทีม
    sessionStorage.setItem('bts_goto_tab', 'team');
    navigate('/game/bts', { replace: true });
  };

  return (
    <div className="bts-receipt-page">
      {step === 'processing' && !popup && (
        <ScanProcessing />
      )}
      {step === 'success' && !popup && (
        <ScanSuccess onClose={handleClose} onCreateTeam={handleCreateTeam} result={result} />
      )}

      {popup && (
        <BtsPopup
          title={popup.title}
          message={popup.message}
          type={popup.type}
          onClose={handleClose}
          actions={[
            { label: 'กลับหน้าหลัก', primary: true, onClick: handleClose },
          ]}
        />
      )}

      {campaignEnded && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '20px 24px 24px',
            maxWidth: 340, width: '100%', textAlign: 'center',
            border: '3px solid #10C851', boxShadow: '4px 4px 0 #10C851',
          }}>
            <img
              src={closedLogo}
              alt="Big C Back to School"
              style={{
                width: '60%',
                maxWidth: 200,
                aspectRatio: '1 / 1',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto 12px',
              }}
            />
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 12px', color: '#111', lineHeight: 1.4 }}>
              บิลรู้ใจคุณ เปิดเทอมใหญ่ หัวใจว้าวุ่น จบแล้ว!
            </h2>
            <p style={{ fontSize: 14, color: '#444', margin: '0 0 8px', lineHeight: 1.6 }}>
              ขอบคุณทุกคนที่ร่วมสนุกกับเกมบิ๊กซี 😊
            </p>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px', lineHeight: 1.6 }}>
              ทางเราจะแจ้งผลรางวัล 5 ทีมที่ชนะ อีกครั้งเร็วๆ นี้
            </p>
            <p style={{ fontSize: 16, color: '#10C851', fontWeight: 700, margin: 0, lineHeight: 1.5 }}>
              รอลุ้น รางวัล "กินไก่ฟรีทั้งปี" กันได้เลย! 🐔🏆
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptPage;
